// Interface for payment-provider integration (Flouci or Konnect).
//
// IMPLEMENTATION STATUS: Flouci is live (one-shot payments — Flouci has no
// recurring billing, so every period is paid manually). The HTTP calls and
// the secrets live in edge functions (flouci-checkout / flouci-verify); the
// adapter here is a thin invoke wrapper.
//
// Why two providers anticipated: Flouci is the broader-coverage Tunisian
// gateway (cards + Edinar + bank transfer), Konnect is a slicker DX. We
// pick one as default but keep the interface neutral.

import type { BillingCycle, PaymentProvider, PlanCode } from './subscription-service';

export interface CheckoutSessionInput {
  /** The plan + cycle the user picked on the billing page. */
  plan: PlanCode;
  cycle: BillingCycle;
  /** Where the gateway should send the user after success/cancel. */
  successUrl: string;
  cancelUrl: string;
  /** The current tenant id — server-side webhook will use this to update
   *  the right tenant_subscriptions row. */
  tenantId: string;
}

export interface CheckoutSession {
  /** URL to redirect the user to so they can pay. */
  redirectUrl: string;
  /** Provider's session/token id, useful for debugging. */
  providerSessionId: string;
  provider: PaymentProvider;
}

/** Result of settling the tenant's pending payments after the customer comes
 *  back from the gateway (or on demand). */
export interface PaymentVerification {
  /** True when a payment was confirmed and the subscription period extended
   *  during THIS call (already-settled payments report activated=false but
   *  outcome='already_paid'). */
  activated: boolean;
  outcome: 'paid' | 'already_paid' | 'pending' | 'failed' | 'expired' | 'none';
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
}

export interface IPaymentService {
  /** The provider this implementation is wired against. Allows the UI to
   *  show "Paying with Flouci" without hard-coding it. */
  readonly provider: PaymentProvider;

  /** Open a hosted-checkout session and return the URL to redirect to.
   *  The actual payment + subscription activation is settled server-side
   *  (verify call / webhook / daily sweep). */
  createCheckoutSession(input: CheckoutSessionInput): Promise<CheckoutSession>;

  /** Server-side verify of the caller tenant's pending payments (optionally
   *  narrowed to one). Call when the user returns from the gateway. */
  verifyPendingPayments(paymentId?: string): Promise<PaymentVerification>;
}

/** Sentinel thrown by the stub adapter so the UI can show a "coming soon"
 *  state cleanly instead of crashing. */
export class PaymentNotConfiguredError extends Error {
  readonly code = 'payment_not_configured';
  constructor(message = 'No payment provider is configured yet.') {
    super(message);
    this.name = 'PaymentNotConfiguredError';
  }
}
